/* global SideDrawer */

if (!customElements.get('quick-add-drawer')) {
  class QuickAddDrawer extends SideDrawer {
    constructor() {
      super();
      this.content = this.querySelector('.js-product-details');
      this.footer = this.querySelector('.drawer__footer');
      this.footerContent = this.querySelector('.js-footer-content');
      this.notification = this.querySelector('.js-added-to-cart');
      this.backBtn = this.querySelector('.drawer__back-btn');
      this.openCartDrawerLinks = this.querySelectorAll('.js-open-cart-drawer');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.fetch = null;
      this.fetchedUrls = [];
      this.quickAddButtonMouseEnterHandler = this.handleQuickAddButtonMouseEnter.bind(this);
      this.documentClickHandler = this.handleDocumentClick.bind(this);

      document.addEventListener('click', this.documentClickHandler);
      this.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      this.openCartDrawerLinks.forEach((link) => {
        link.addEventListener('click', this.handleOpenCartClick.bind(this));
      });

      if (theme.device.hasHover && theme.mediaMatches.md) {
        document.querySelectorAll('.js-quick-add').forEach((button) => {
          this.bindQuickAddButtonMouseEnter(button);
        });

        if ('MutationObserver' in window) {
          this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              // Add event listener to new buttons
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    this.bindQuickAddButtonMouseEnter(button);
                  });
                }
              });

              // Remove event listener from removed buttons
              mutation.removedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
                  });
                }
              });
            });
          });

          // Start observing changes to the DOM
          this.observer.observe(document.body, { childList: true, subtree: true });
        }
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.documentClickHandler);
      document.querySelectorAll('.js-quick-add').forEach((button) => {
        button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      });
      if (this.observer) this.observer.disconnect();
    }

    /**
     * Bind a mouseenter event to a given quick add button if it's not already bound
     * @param {Element} button - The button element to bind the mouseenter event to.
     */
    bindQuickAddButtonMouseEnter(button) {
      if (!button.dataset.quickAddListenerAdded) {
        button.dataset.quickAddListenerAdded = 'true';
        button.addEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      }
    }

    /**
     * Start the fetch on hover of a quick add button for perceived performance gains
     * @param {object} evt - Event object.
     */
    handleQuickAddButtonMouseEnter(evt) {
      if (!this.fetchedUrls.includes(evt.target.dataset.productUrl)) {
        this.fetch = {
          url: evt.target.dataset.productUrl,
          promise: fetch(evt.target.dataset.productUrl)
        };
        this.fetchedUrls.push(evt.target.dataset.productUrl);
      }
    }

    /**
     * Handles 'click' events on success notification's 'View cart' link
     * @param {object} evt - Event object.
     */
    handleOpenCartClick(evt) {
      // Open the cart drawer if available on the page
      if (this.cartDrawer) {
        evt.preventDefault();
        this.cartDrawer.open();
      } else if (window.location.pathname === theme.routes.cart) {
        evt.preventDefault();
        this.close();
      }
    }

    /**
     * Handles 'click' events on the document.
     * @param {object} evt - Event object.
     */
    handleDocumentClick(evt) {
      if (!evt.target.matches('.js-quick-add')) return;

      // Close the cart drawer if it's open
      if (this.cartDrawer && this.cartDrawer.ariaHidden === 'false') {
        const overlay = document.querySelector('.js-overlay.is-visible');
        if (overlay) overlay.style.transitionDelay = '200ms';

        this.cartDrawer.close();

        // Wait a few ms for a more optimal ux/animation
        setTimeout(() => {
          this.backBtn.hidden = false;
          this.open(evt.target);
          if (overlay) overlay.style.transitionDelay = '';
        }, 200);
      } else {
        this.open(evt.target);
      }
    }

    /**
     * Handles 'on:variant:change' events on the Quick Add drawer.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      let url = this.productUrl;

      if (evt.detail.variant) {
        const separator = this.productUrl.split('?').length > 1 ? '&' : '?';
        url += `${separator}variant=${evt.detail.variant.id}`;
      }

      this.querySelectorAll('.js-prod-link').forEach((link) => {
        link.href = url;
      });

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        this.updateMedia(evt.detail.variant.featured_media);
      }
    }

    /**
     * Opens the drawer and fetches the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    async open(opener) {
      opener.setAttribute('aria-disabled', 'true');

      if (this.notification) this.notification.hidden = true;

      // If it's the same product as previously shown, there's no need to re-fetch the details.
      if (this.productUrl && this.productUrl === opener.dataset.productUrl) {
        super.open(opener);
        opener.removeAttribute('aria-disabled');
        return;
      }

      this.productUrl = opener.dataset.productUrl;
      this.content.innerHTML = '';
      this.classList.add('is-loading');
      this.content.classList.add('drawer__content--out');
      this.footer.classList.add('drawer__footer--out');

      super.open(opener);

      if (!this.fetch || this.fetch.url !== opener.dataset.productUrl) {
        this.fetch = {
          url: opener.dataset.productUrl,
          promise: fetch(opener.dataset.productUrl)
        };
      }

      // Sanos (Claude 2026-11-03): Produktseiten mit eigener Vorlage (sanos-pdp-mobile) liefern keine
      // Schnellkauf-Vorlage. Dann eigener Schnellkauf (renderSanos), sonst direkt zur Produktseite.
      const goToProduct = (variantId) => {
        this.fetch = null;
        this.productUrl = null;
        opener.removeAttribute('aria-disabled');
        const url = opener.dataset.productUrl;
        window.location.href = variantId ? `${url}${url.indexOf('?') > -1 ? '&' : '?'}variant=${variantId}` : url;
      };

      let tmpl = null;
      let quickTpl = null;
      try {
        const response = await this.fetch.promise;
        if (response.ok) {
          tmpl = document.createElement('template');
          tmpl.innerHTML = await response.clone().text();
          quickTpl = tmpl.content.getElementById('quick-buy-template');
        }
      } catch (e) {
        quickTpl = null;
      }

      if (!quickTpl || !quickTpl.content || !quickTpl.content.querySelector('.js-product')) {
        this.fetch = null;
        if (!tmpl || !this.renderSanos(tmpl.content, opener, goToProduct)) goToProduct();
        opener.removeAttribute('aria-disabled');
        return;
      }

      if (this.sqbClick) { this.footerContent.removeEventListener('click', this.sqbClick); this.sqbClick = null; }
      this.productEl = quickTpl.content.querySelector('.js-product');
      this.renderProduct(opener);

      this.fetch = null;

      opener.removeAttribute('aria-disabled');
    }

    /**
     * Sanos-Schnellkauf: baut Auswahl + „In den Warenkorb“ aus den Daten der Sanos-Produktseite.
     * Artikel mit Pflicht-Zusätzen (Beschriftung, Schnittgebühr, Zuschnitt/Ring) gehen auf die Produktseite.
     * @param {DocumentFragment} doc - Geladene Produktseite.
     * @param {Element} opener - Auslösender Button.
     * @param {Function} goToProduct - Weiterleitung zur Produktseite.
     * @returns {boolean} true, wenn der Schnellkauf angezeigt wird.
     */
    renderSanos(doc, opener, goToProduct) {
      const dataEl = doc.querySelector('[data-sp-data]');
      if (!dataEl) return false;
      let D;
      try { D = JSON.parse(dataEl.textContent); } catch (e) { return false; }
      const variants = D.variants || [];
      if (!variants.length) return false;
      const A = D.addons || {};
      const R = D.ring || {};
      if ((A.enabled && ((A.label && A.label.enabled) || (A.cut && A.cut.enabled))) || R.product) return false;
      const ringActive = (v) => {
        if (!R.possible || !v) return false;
        const t = String(v.title || '').toLowerCase();
        return (R.variantKeywords || []).some((k) => { const s = String(k).trim().toLowerCase(); return s && t.indexOf(s) !== -1; });
      };

      QuickAddDrawer.injectSanosCss();
      const esc = (x) => String(x == null ? '' : x).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      const money = (c) => `${(Math.round(c) / 100).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')} ${D.cur || '€'}`;
      const url = D.productUrl || opener.dataset.productUrl;
      const T = D.text || {};

      // Auswahlfelder (Namen + Reihenfolge der Werte) aus der Produktseite übernehmen
      const n = D.optionCount || (variants[0].options || []).length;
      const pageSelects = Array.prototype.slice.call(doc.querySelectorAll('[data-sp-option]'));
      const opts = [];
      for (let i = 0; i < n; i += 1) {
        const ps = pageSelects[i];
        let values = [];
        let name = `Option ${i + 1}`;
        let placeholder = T.selectPlaceholder || 'Bitte wählen';
        if (ps) {
          name = ps.getAttribute('aria-label') || name;
          Array.prototype.forEach.call(ps.options, (o) => {
            if (!o.value) placeholder = o.textContent.trim() || placeholder;
            else if (values.indexOf(o.value) === -1) values.push(o.value);
          });
        }
        variants.forEach((v) => { if (values.indexOf(v.options[i]) === -1) values.push(v.options[i]); });
        values = values.filter((val) => variants.some((v) => v.options[i] === val));
        opts.push({ name, values, placeholder });
      }
      const single = variants.length === 1;
      const sel = single ? variants[0].options.slice() : new Array(n).fill('');

      const matches = (v, upto) => {
        for (let j = 0; j < upto; j += 1) { if (sel[j] && v.options[j] !== sel[j]) return false; }
        return true;
      };
      const valid = (i) => opts[i].values.filter((val) => variants.some((v) => v.options[i] === val && matches(v, i)));
      const findVariant = () => (sel.every(Boolean) ? variants.find((v) => v.options.every((o, j) => o === sel[j])) || null : null);
      const settle = (from) => {
        for (let j = from; j < n; j += 1) {
          const ok = valid(j);
          if (sel[j] && ok.indexOf(sel[j]) === -1) sel[j] = '';
          if (!sel[j] && ok.length === 1) sel[j] = ok[0];
        }
      };
      settle(0);

      const firstImg = doc.querySelector('.sp-gallery__slide img');
      const baseImg = firstImg ? (firstImg.getAttribute('src') || '') : '';
      const imgOf = (v) => {
        const m = v && v.featured_media && v.featured_media.preview_image;
        return m && m.src ? `${m.src}${m.src.indexOf('?') > -1 ? '&' : '?'}width=600` : baseImg;
      };

      this.content.innerHTML = `
        <div class="sqb">
          <a class="sqb__media js-prod-link" href="${esc(url)}"><img src="${esc(baseImg)}" alt="${esc(D.productTitle)}" loading="eager"></a>
          <div class="sqb__head">
            <a class="sqb__title js-prod-link" href="${esc(url)}">${esc(D.productTitle)}</a>
            <div class="sqb__price" data-sqb-price></div>
            <div class="sqb__tax">inkl. MwSt. zzgl. Versand</div>
          </div>
          <div class="sqb__opts" data-sqb-opts></div>
          <div class="sqb__stock" data-sqb-stock></div>
          <a class="sqb__more js-prod-link" href="${esc(url)}">Alle Details ansehen</a>
        </div>`;
      this.footerContent.innerHTML = `
        <div class="sqb-buy">
          <div class="sqb-buy__qty">
            <button type="button" data-sqb-qty="-1" aria-label="Menge verringern">&minus;</button>
            <input type="number" min="1" value="1" inputmode="numeric" aria-label="Menge" data-sqb-qty-input>
            <button type="button" data-sqb-qty="1" aria-label="Menge erhöhen">+</button>
          </div>
          <button type="button" class="btn btn--primary sqb-buy__add" data-sqb-add><span></span></button>
        </div>
        <div class="sqb-buy__error" data-sqb-error hidden></div>`;
      this.footer.classList.remove('quick-add__footer-message');

      const box = this.content.querySelector('[data-sqb-opts]');
      const priceEl = this.content.querySelector('[data-sqb-price]');
      const stockEl = this.content.querySelector('[data-sqb-stock]');
      const imgEl = this.content.querySelector('.sqb__media img');
      const addBtn = this.footerContent.querySelector('[data-sqb-add]');
      const addLabel = addBtn.querySelector('span');
      const qtyInput = this.footerContent.querySelector('[data-sqb-qty-input]');
      const errEl = this.footerContent.querySelector('[data-sqb-error]');
      const links = this.querySelectorAll('.js-prod-link');

      const stockText = (v) => {
        if (!v.available) return { cls: 'is-out', txt: T.unavailable || 'Derzeit nicht verfügbar' };
        const inv = (D.inventory || {})[v.id];
        if (inv && inv.tracked && inv.qty <= 0 && inv.policy === 'continue') return { cls: 'is-back', txt: T.backorder || 'Nachbestellt' };
        return { cls: 'is-in', txt: T.instock || 'Auf Lager' };
      };
      const renderOpts = () => {
        if (single) { box.innerHTML = ''; return; }
        box.innerHTML = opts.map((o, i) => {
          const ok = valid(i);
          const options = ok.map((val) => {
            const avail = variants.some((v) => v.options[i] === val && matches(v, i) && v.available);
            return `<option value="${esc(val)}"${sel[i] === val ? ' selected' : ''}>${esc(val)}${avail ? '' : ' – nicht verfügbar'}</option>`;
          }).join('');
          return `<label class="sqb__opt"><span class="sqb__opt-l">${esc(o.name)}</span>
            <select class="sqb__select" data-sqb-opt="${i}"><option value=""${sel[i] ? '' : ' selected'}>${esc(o.placeholder)}</option>${options}</select></label>`;
        }).join('');
      };
      const render = () => {
        renderOpts();
        const v = findVariant();
        let href = url;
        if (v) {
          href = `${url}${url.indexOf('?') > -1 ? '&' : '?'}variant=${v.id}`;
          const cmp = v.compare_at_price && v.compare_at_price > v.price ? `<s>${money(v.compare_at_price)}</s>` : '';
          priceEl.innerHTML = `${money(v.price)} ${cmp}`;
          const s = stockText(v);
          stockEl.className = `sqb__stock ${s.cls}`;
          stockEl.textContent = s.txt;
          stockEl.hidden = false;
          const src = imgOf(v);
          if (src && imgEl.getAttribute('src') !== src) imgEl.setAttribute('src', src);
        } else {
          priceEl.innerHTML = `${variants.length > 1 ? 'ab ' : ''}${money(D.priceMin || Math.min.apply(null, variants.map((x) => x.price)))}`;
          stockEl.hidden = true;
        }
        links.forEach((l) => { l.href = href; });

        addBtn.removeAttribute('data-sqb-mode');
        if (!v) {
          const miss = opts.find((o, i) => !sel[i]);
          addLabel.textContent = miss ? `Bitte „${miss.name}“ wählen` : (T.selectFirst || 'Bitte Variante wählen');
          addBtn.setAttribute('aria-disabled', 'true');
        } else if (!v.available) {
          addLabel.textContent = T.unavailable || 'Derzeit nicht verfügbar';
          addBtn.setAttribute('aria-disabled', 'true');
        } else if (ringActive(v)) {
          addLabel.textContent = 'Weiter zur Produktseite';
          addBtn.setAttribute('data-sqb-mode', 'page');
          addBtn.removeAttribute('aria-disabled');
        } else {
          addLabel.textContent = `In den Warenkorb · ${money(v.price)}`;
          addBtn.removeAttribute('aria-disabled');
        }
      };

      box.addEventListener('change', (evt) => {
        const s = evt.target.closest('[data-sqb-opt]');
        if (!s) return;
        const i = parseInt(s.getAttribute('data-sqb-opt'), 10);
        sel[i] = s.value;
        settle(i + 1);
        errEl.hidden = true;
        render();
      });
      if (this.sqbClick) this.footerContent.removeEventListener('click', this.sqbClick);
      this.sqbClick = (evt) => {
        const q = evt.target.closest('[data-sqb-qty]');
        if (q) {
          qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + parseInt(q.getAttribute('data-sqb-qty'), 10));
          return;
        }
        if (!evt.target.closest('[data-sqb-add]')) return;
        const v = findVariant();
        if (!v) {
          const i = opts.findIndex((o, k) => !sel[k]);
          const s = box.querySelector(`[data-sqb-opt="${i}"]`);
          if (s) {
            const lab = s.closest('.sqb__opt');
            lab.classList.remove('is-attention'); void lab.offsetWidth; lab.classList.add('is-attention');
            s.focus();
          }
          return;
        }
        if (addBtn.getAttribute('aria-disabled') === 'true') return;
        if (addBtn.getAttribute('data-sqb-mode') === 'page') { goToProduct(v.id); return; }
        this.addSanos(v, Math.max(1, parseInt(qtyInput.value, 10) || 1), addBtn, errEl);
      };
      this.footerContent.addEventListener('click', this.sqbClick);

      render();
      this.classList.remove('is-loading');
      this.content.classList.remove('drawer__content--out');
      this.footer.classList.remove('drawer__footer--out');
      return true;
    }

    /**
     * Legt die gewählte Variante in den Warenkorb (wie das Theme-Formular: Zähler, Hinweis, Warenkorb-Seite aktualisieren).
     */
    async addSanos(v, qty, btn, errEl) {
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('is-loading');
      errEl.hidden = true;
      try {
        const res = await fetch(`${theme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: v.id, quantity: qty }], sections: 'cart-icon-bubble', sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (!res.ok) throw new Error((data && (data.description || data.message)) || 'Der Artikel konnte nicht in den Warenkorb gelegt werden.');
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && data.sections && data.sections['cart-icon-bubble']) bubble.innerHTML = data.sections['cart-icon-bubble'];
        btn.classList.add('is-success');
        this.addedToCart();
        setTimeout(() => {
          if (this.cartDrawer) {
            if (typeof this.cartDrawer.refresh === 'function') this.cartDrawer.refresh();
          } else if (window.location.pathname === theme.routes.cart) {
            const cartItems = document.querySelector('cart-items');
            if (cartItems) {
              if (cartItems.dataset.empty === 'true') window.location.reload();
              else cartItems.refresh();
            }
          }
        }, 700);
        try {
          const cart = await (await fetch(`${theme.routes.cart}.js`)).json();
          this.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { cart, variantId: v.id } }));
        } catch (e) { /* Zähler ist bereits aktualisiert */ }
      } catch (err) {
        errEl.textContent = err.message;
        errEl.hidden = false;
      } finally {
        btn.removeAttribute('aria-disabled');
        setTimeout(() => { btn.classList.remove('is-loading'); btn.classList.remove('is-success'); }, 1400);
      }
    }

    static injectSanosCss() {
      if (document.getElementById('sqb-css')) return;
      const st = document.createElement('style');
      st.id = 'sqb-css';
      st.textContent = `
        .sqb { display: grid; gap: 14px; padding-bottom: 8px; }
        .sqb__media { display: block; border: 1px solid rgba(0,0,0,.08); border-radius: 10px; background: #fff; overflow: hidden; }
        .sqb__media img { display: block; width: 100%; height: auto; max-height: 300px; object-fit: contain; }
        .sqb__title { display: block; color: inherit; font-weight: 700; font-size: 16px; line-height: 1.35; text-decoration: none; }
        .sqb__price { margin-top: 6px; font-size: 20px; font-weight: 700; }
        .sqb__price s { margin-left: 6px; font-size: 14px; font-weight: 400; opacity: .55; }
        .sqb__tax { font-size: 12px; opacity: .65; }
        .sqb__opts { display: grid; gap: 10px; }
        .sqb__opt { display: grid; gap: 5px; margin: 0; }
        .sqb__opt-l { font-size: 13px; font-weight: 700; }
        .sqb__select { width: 100%; height: 46px; padding: 0 38px 0 12px; border: 1.5px solid #d9d9d9; border-radius: 10px; background: #fff; color: #1a1a1a; font: inherit; font-size: 14px;
          -webkit-appearance: none; appearance: none; cursor: pointer;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23555' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: right 12px center; background-size: 16px; }
        .sqb__select:focus { outline: none; border-color: #87BF61; box-shadow: 0 0 0 3px rgba(135,191,97,.25); }
        .sqb__opt.is-attention .sqb__opt-l { color: #c62828; }
        .sqb__opt.is-attention .sqb__select { border-color: #c62828; box-shadow: 0 0 0 3px rgba(198,40,40,.18); animation: sqb-shake .45s ease 1; }
        @keyframes sqb-shake { 0%,100% { transform: translateX(0); } 25% { transform: translateX(-5px); } 50% { transform: translateX(5px); } 75% { transform: translateX(-3px); } }
        .sqb__stock { display: flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 600; }
        .sqb__stock::before { content: ''; width: 8px; height: 8px; border-radius: 50%; background: #2e8b57; }
        .sqb__stock.is-back::before { background: #e0a800; }
        .sqb__stock.is-out::before { background: #9a9a9a; }
        .sqb__stock[hidden] { display: none; }
        .sqb__more { font-size: 13px; color: inherit; text-decoration: underline; }
        .sqb-buy { display: flex; gap: 8px; align-items: stretch; }
        .sqb-buy__qty { flex: 0 0 auto; display: inline-flex; align-items: center; height: 50px; border: 1.5px solid #d9d9d9; border-radius: 10px; background: #fff; overflow: hidden; }
        .sqb-buy__qty button { width: 38px; height: 100%; padding: 0; border: 0; background: transparent; color: #333; font-size: 19px; line-height: 1; cursor: pointer; }
        .sqb-buy__qty input { width: 40px; height: 100%; padding: 0; border: 0; background: transparent; font: inherit; font-size: 15px; font-weight: 600; text-align: center; -moz-appearance: textfield; }
        .sqb-buy__qty input::-webkit-outer-spin-button, .sqb-buy__qty input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        .sqb-buy__add { flex: 1 1 auto; min-width: 0; min-height: 50px; white-space: nowrap; }
        .sqb-buy__add span { display: block; overflow: hidden; text-overflow: ellipsis; }
        .sqb-buy__add[aria-disabled="true"] { opacity: .55; }
        .sqb-buy__error { margin-top: 8px; font-size: 13px; color: #c62828; }
        .sqb-buy__error[hidden] { display: none; }`;
      document.head.appendChild(st);
    }

    /**
     * Closes the cart drawer.
     */
    close() {
      super.close(() => {
        this.backBtn.hidden = true;
      });
    }

    /**
     * Renders the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    renderProduct(opener) {
      QuickAddDrawer.convertToQuickBuyContent(this.productEl, this.productUrl);

      // Check whether to select the first variant.
      const variantPicker = this.productEl.querySelector('variant-picker');
      if (variantPicker) {
        this.selectFirstVariant = variantPicker.dataset.selectFirstVariant === 'true';
      }

      this.updateFooter();
      this.updateContent();

      if (opener.dataset.selectedColor && this.selectFirstVariant) {
        // Timeout to allow the VariantPicker to initialize
        setTimeout(this.setActiveVariant.bind(this, opener), 10);
      }
    }

    /**
     * Set color variant to match the one selected in the card.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    setActiveVariant(opener) {
      const colorOptionBox = this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`);
      if (colorOptionBox) {
        this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`)
          .click();
      } else {
        const colorOptionDropdown = this.querySelector(
          `.custom-select__option[data-value="${opener.dataset.selectedColor}"]`
        );
        if (colorOptionDropdown) {
          const customSelect = colorOptionDropdown.closest('custom-select');
          customSelect.selectOption(colorOptionDropdown);
        }
      }
    }

    /**
     * Updates the product media.
     * @param {string} media - Variant featured media object.
     */
    updateMedia(media) {
      if (!media) return;

      const { src } = media.preview_image;
      const container = this.querySelector('.quick-add-info__media');
      const width = container.offsetWidth;
      const aspectRatio = media.preview_image.aspect_ratio;

      const existingId = container.dataset.mediaId;
      const existingImg = container.querySelector('img');

      if (!existingImg || existingId !== `${media.id}`) {
        container.innerHTML = `
          <img src="${src}&width=${width}" srcset="${src}&width=${width}, ${src}&width=${width * 2} 2x" width="${width * 2}" height="${(width * 2) / aspectRatio}" alt="${media.alt || ''}">
        `;
        container.dataset.mediaId = media.id;
      }
    }

    /**
     * Renders the markup for the drawer content element.
     */
    updateContent() {
      const productContent = this.productEl.querySelector('.js-product-details');
      if (productContent) {
        this.content.innerHTML = productContent.innerHTML;
      }

      this.classList.remove('is-loading');
      this.content.classList.remove('drawer__content--out');
    }

    /**
     * Renders the markup for the drawer footer element.
     */
    updateFooter() {
      const footerContent = this.productEl.querySelector('.js-footer-content');
      this.footer.classList.remove('quick-add__footer-message');

      if (footerContent && footerContent.hasChildNodes()) {
        this.footerContent.innerHTML = footerContent.innerHTML;
      } else {
        this.footer.classList.add('quick-add__footer-message');
        this.footerContent.innerHTML = `
          <div class="alert quick-add__alert bg-info-bg text-info-text">
            <div class="flex">
              <div>
                <svg class="icon icon--price_tag" width="32" height="32" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                  <path fill="currentColor" d="M7.59 1.34a1 1 0 01.7-.29h5.66a1 1 0 011 1v5.66a1 1 0 01-.3.7L7.6 15.5a1 1 0 01-1.42 0L.52 9.83a1 1 0 010-1.42l7.07-7.07zm6.36 6.37l-7.07 7.07-5.66-5.66L8.3 2.05h5.66v5.66z" fill-rule="evenodd"/>
                  <path fill="currentColor" d="M9.7 6.3a1 1 0 101.42-1.42 1 1 0 00-1.41 1.41zM9 7a2 2 0 102.83-2.83A2 2 0 009 7z" fill-rule="evenodd"/>
                </svg>
              </div>
              <div>
                <p class="text-h6 font-bold mb-2">${theme.strings.awaitingSale}</p>
                <a class="link js-prod-link" href="${this.productUrl}">${theme.strings.viewDetails}</a>
              </div>
            </div>
          </div>`;
      }

      this.footer.classList.remove('drawer__footer--out');
    }

    /**
     * Shows an "Added to cart" message in the drawer.
     */
    addedToCart() {
      if (this.notification) {
        setTimeout(() => {
          this.notification.hidden = false;
        }, 300);

        setTimeout(() => {
          this.notification.hidden = true;
        }, this.notification.dataset.visibleFor);
      }
    }

    /**
     * Convert product template content to quick buy content.
     * @param {Element} container - Container element containing new content to use.
     */
    static convertToQuickBuyContent(container) {
      // Prevent variant picker from updating the URL on change.
      const variantPicker = container.querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.dataset.updateUrl = 'false';
      }

      // Remove size chart modal and link (if they exist).
      const sizeChartModal = container.querySelector('[data-modal="size-chart"]');
      if (sizeChartModal) {
        sizeChartModal.remove();
      }
    }
  }

  customElements.define('quick-add-drawer', QuickAddDrawer);

  window.theme = window.theme || {};
  window.theme.quickBuy = window.theme.quickBuy || {};
  window.theme.quickBuy.convertToQuickBuyContent = QuickAddDrawer.convertToQuickBuyContent;
}
